import { t } from '@lingui/macro';
import { Moment } from 'moment';

import { useState, useEffect, useCallback } from 'react';

import { formatDateToMMDDYYYY } from '../../../helpers/dateUtils';
import { ExpirationOption, ExpirationOptionId } from '../types';

/**
 * Custom hook for managing expiration date logic and calculations
 * Handles multiple related states, date calculations, and user interactions
 */
export const useExpirationDate = (): {
  expirationOption: ExpirationOptionId;
  setExpirationOption: (value: ExpirationOptionId) => void;
  customDate: string;
  setCustomDate: (value: string) => void;
  expiryDate: string;
  setExpiryDate: (value: string) => void;
  expirationOptions: ExpirationOption[];
  calculateExpiryDate: (option: ExpirationOptionId, customDateValue?: string) => string;
  handleExpirationChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleCustomDateChange: (dateValue: string | Moment) => void;
} => {
  const [expirationOption, setExpirationOption] = useState<ExpirationOptionId>('1_year');
  const [customDate, setCustomDate] = useState<string>('');
  const [expiryDate, setExpiryDate] = useState<string>('');

  // Predefined expiration options
  const expirationOptions: ExpirationOption[] = [
    { id: '7_days', label: t`In 7 days`, days: 7 },
    { id: '30_days', label: t`In 30 days`, days: 30 },
    { id: '90_days', label: t`In 90 days`, days: 90 },
    { id: '1_year', label: t`In 1 year`, days: 365 },
  ];

  /**
   * Calculates expiry date based on selected option or custom date
   * Returns MM/DD/YYYY format for display consistency
   */
  const calculateExpiryDate = useCallback((
    option: ExpirationOptionId,
    customDateValue: string = customDate
  ): string => {
    let displayDate = '';

    if (option === 'custom_date') {
      displayDate = formatDateToMMDDYYYY(new Date(customDateValue));
    } else {
      const selectedOption = expirationOptions.find((opt) => opt.id === option);
      if (selectedOption) {
        const date = new Date();
        date.setDate(date.getDate() + selectedOption.days);
        displayDate = formatDateToMMDDYYYY(date);
      }
    }

    return displayDate;
  }, [customDate]);

  /**
   * Handles expiration option selection change
   */
  const handleExpirationChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newOption = e.target.value as ExpirationOptionId;
    setExpirationOption(newOption);
  };

  /**
   * Handles custom date picker value changes
   */
  const handleCustomDateChange = useCallback((dateValue: string | Moment): void => {
    const formattedDate = typeof dateValue === 'string'
      ? dateValue
      : dateValue.format('MM/DD/YYYY');

    setCustomDate(formattedDate);
    const newExpiryDate = calculateExpiryDate('custom_date', formattedDate);
    setExpiryDate(newExpiryDate);
  }, [calculateExpiryDate]);

  /**
   * Updates expiry date when user changes expiration option
   */
  useEffect(() => {
    const newExpiryDate = calculateExpiryDate(expirationOption);
    setExpiryDate(newExpiryDate);
  }, [expirationOption, calculateExpiryDate]);

  return {
    expirationOption,
    setExpirationOption,
    customDate,
    setCustomDate,
    expiryDate,
    setExpiryDate,
    expirationOptions,
    calculateExpiryDate,
    handleExpirationChange,
    handleCustomDateChange
  };
};
